import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Grid,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorIcon,
  Shield as ShieldIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Key as KeyIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { 
  type User, 
  UserRole, 
  type CreateUserRequest, 
  type UpdateUserRequest,
  needsPasswordSetup 
} from '../../types/auth';
import { useUserAdminService } from '../../services/userAdminService';

interface UserFormData extends Omit<CreateUserRequest, 'sendWelcomeEmail'> {
  sendWelcomeEmail: boolean; // ✅ Renombrado para claridad
}

const UserManagement: React.FC = () => {
  const { jwtToken } = useAuth();
  const userService = useUserAdminService();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    fullName: '',
    email: '',
    password: '', // ✅ Opcional
    roles: [],
    isActive: true,
    sendWelcomeEmail: true // ✅ Por defecto para usuarios nuevos
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  // Cargar usuarios al montar el componente
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch users from API
  const fetchUsers = async () => {
    if (!jwtToken) return;
    
    try {
      setLoading(true);
      const result = await userService.getAllUsers();
      
      if (result.ok) {
        setUsers(result.data);
      } else {
        setAlert({ type: 'error', message: result.error || 'Error al cargar usuarios' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Error de conexión' });
    } finally {
      setLoading(false);
    }
  };

  // Filtrar usuarios por término de búsqueda
  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Abrir diálogo para crear/editar usuario
  const handleOpenDialog = (user?: User) => {
    if (user) {
      // ✅ PROTECCIÓN FRONTEND: Verificar si es el usuario admin
      if (user.username === 'admin') {
        setAlert({ type: 'error', message: 'No se puede modificar el usuario administrador principal' });
        return;
      }
      
      setEditingUser(user);
      setFormData({
        username: user.username,
        fullName: user.fullName,
        email: user.email,
        password: '', // ✅ Vacío para edición
        roles: user.roles,
        isActive: user.isActive || true,
        sendWelcomeEmail: false // ✅ No enviar email al editar
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        fullName: '',
        email: '',
        password: '', // ✅ Vacío para nuevo usuario (se enviará email)
        roles: [],
        isActive: true,
        sendWelcomeEmail: true // ✅ Enviar email para usuarios nuevos por defecto
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  // Cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({
      username: '',
      fullName: '',
      email: '',
      password: '',
      roles: [],
      isActive: true,
      sendWelcomeEmail: true
    });
    setFormErrors({});
  };

  // Validar formulario
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.username.trim()) {
      errors.username = 'El nombre de usuario es requerido';
    }
    if (!formData.fullName.trim()) {
      errors.fullName = 'El nombre completo es requerido';
    }
    if (!formData.email.trim()) {
      errors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'El email no es válido';
    }
    
    // ✅ NUEVA LÓGICA: Validación condicional de contraseña
    if (editingUser && formData.password && formData.password.length > 0 && formData.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }
    
    if (formData.roles.length === 0) {
      errors.roles = 'Debe asignar al menos un rol';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Guardar usuario (crear o actualizar)
  const handleSaveUser = async () => {
    if (!validateForm() || !jwtToken) return;

    setSubmitLoading(true);
    try {
      let result;
      
      if (editingUser) {
        // ✅ ACTUALIZAR usuario existente
        const updateData: UpdateUserRequest = {
          fullName: formData.fullName,
          email: formData.email,
          roles: formData.roles,
          isActive: formData.isActive,
          ...(formData.password && formData.password.trim().length > 0 && { password: formData.password })
        };
        
        result = await userService.updateUser(editingUser.id, updateData);
      } else {
        // ✅ CREAR nuevo usuario
        const createData: CreateUserRequest = {
          username: formData.username,
          fullName: formData.fullName,
          email: formData.email,
          roles: formData.roles,
          isActive: formData.isActive,
          sendWelcomeEmail: formData.sendWelcomeEmail,
          ...(formData.password && formData.password.trim().length > 0 && { password: formData.password })
        };
        
        result = await userService.createUser(createData);
      }

      if (result.ok) {
        let message = editingUser 
          ? 'Usuario actualizado exitosamente' 
          : 'Usuario creado exitosamente';
          
        // ✅ MENSAJE ESPECÍFICO para usuarios nuevos sin contraseña
        if (!editingUser && !formData.password && formData.sendWelcomeEmail) {
          message += '. Se ha enviado un email para que el usuario establezca su contraseña.';
        }
            
        setAlert({ type: 'success', message });
        handleCloseDialog();
        fetchUsers();
      } else {
        setAlert({ type: 'error', message: result.error || 'Error al guardar usuario' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Error de conexión' });
    } finally {
      setSubmitLoading(false);
    }
  };

  // ✅ CORREGIDO: Reenviar email usando el servicio
  const handleSendPasswordSetup = async (userEmail: string) => {
    try {
      const result = await userService.sendPasswordSetupEmail(userEmail);

      if (result.ok && result.data && result.data.success) { // ✅ CORREGIDO: Verificar null
        setAlert({ 
          type: 'success', 
          message: `Email de configuración de contraseña enviado a ${userEmail}` 
        });
      } else {
        setAlert({ 
          type: 'error', 
          message: result.error || result.data?.message || 'Error al enviar email de configuración' 
        });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Error de conexión' });
    }
  };

  // Eliminar usuario
  const handleDeleteUser = async (userId: number, username: string) => {
    // ✅ PROTECCIÓN FRONTEND: Verificar si es el usuario admin
    if (username === 'admin') {
      setAlert({ type: 'error', message: 'No se puede eliminar el usuario administrador principal' });
      return;
    }

    if (!confirm('¿Estás seguro de que quieres eliminar este usuario?') || !jwtToken) return;

    try {
      const result = await userService.deleteUser(userId);

      if (result.ok) {
        setAlert({ type: 'success', message: 'Usuario eliminado exitosamente' });
        fetchUsers();
      } else {
        setAlert({ type: 'error', message: result.error || 'Error al eliminar usuario' });
      }
    } catch (error) {
      setAlert({ type: 'error', message: 'Error de conexión' });
    }
  };

  // Obtener icono por rol
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'ADMIN': return <AdminIcon fontSize="small" />;
      case 'SUPERVISOR': return <SupervisorIcon fontSize="small" />;
      case 'USER': return <PersonIcon fontSize="small" />;
      default: return <PersonIcon fontSize="small" />;
    }
  };

  // Obtener color por rol
  const getRoleColor = (role: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (role) {
      case 'ADMIN': return 'error';
      case 'SUPERVISOR': return 'warning';
      case 'USER': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Gestión de Usuarios
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administra usuarios, roles y permisos del sistema
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ textTransform: 'none' }}
        >
          Nuevo Usuario
        </Button>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Search and Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchUsers}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Actualizar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell><strong>Usuario</strong></TableCell>
                  <TableCell><strong>Nombre Completo</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Roles</strong></TableCell>
                  <TableCell><strong>Estado Contraseña</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.username}
                          </Typography>
                          {/* ✅ INDICADOR VISUAL: Mostrar escudo para usuario protegido */}
                          {user.username === 'admin' && (
                            <Tooltip title="Usuario protegido - No se puede editar ni eliminar">
                              <ShieldIcon 
                                fontSize="small" 
                                sx={{ color: 'warning.main' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.fullName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.roles.map((role) => (
                            <Chip
                              key={role}
                              label={role}
                              color={getRoleColor(role)}
                              size="small"
                              icon={getRoleIcon(role)}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </TableCell>
                      {/* ✅ CORREGIDO: Usar passwordSet del backend */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {user.passwordSet ? (
                            <Chip
                              label="Configurada"
                              color="success"
                              size="small"
                              icon={<CheckCircleIcon />}
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Pendiente"
                              color="warning"
                              size="small"
                              icon={<WarningIcon />}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          {/* ✅ PROTECCIÓN VISUAL: Deshabilitar botones para usuario admin */}
                          <Tooltip title={user.username === 'admin' ? 'No se puede editar el usuario administrador principal' : 'Editar usuario'}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDialog(user)}
                                disabled={user.username === 'admin'}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          
                          {/* ✅ CORREGIDO: Botón para enviar email solo si no tiene contraseña */}
                          {needsPasswordSetup(user) && user.username !== 'admin' && (
                            <Tooltip title="Enviar email de configuración de contraseña">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleSendPasswordSetup(user.email)}
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title={user.username === 'admin' ? 'No se puede eliminar el usuario administrador principal' : 'Eliminar usuario'}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteUser(user.id, user.username)}
                                disabled={user.username === 'admin'}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog for Create/Edit User */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre de Usuario"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  error={!!formErrors.username}
                  helperText={formErrors.username}
                  disabled={!!editingUser}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre Completo"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  error={!!formErrors.fullName}
                  helperText={formErrors.fullName}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>

              {/* ✅ NUEVO: Sección condicional de contraseña */}
              {editingUser && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Contraseña (Opcional)
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nueva Contraseña"
                      type="password"
                      value={formData.password || ''}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value || undefined })}
                      error={!!formErrors.password}
                      helperText={formErrors.password || "Dejar vacío para mantener la contraseña actual"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <KeyIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </>
              )}

              {/* ✅ NUEVO: Opciones para usuarios nuevos */}
              {!editingUser && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Configuración de Contraseña
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid item xs={12}>
                    <Alert 
                      severity="info" 
                      sx={{ mb: 2 }}
                      icon={<EmailIcon />}
                    >
                      <Typography variant="body2">
                        <strong>Recomendado:</strong> Dejar la contraseña vacía y enviar email de bienvenida. 
                        El usuario recibirá un enlace seguro para establecer su propia contraseña.
                      </Typography>
                    </Alert>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Contraseña (Opcional)"
                      type="password"
                      value={formData.password || ''}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value || undefined })}
                      error={!!formErrors.password}
                      helperText={formErrors.password || "Dejar vacío para que el usuario establezca su propia contraseña"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <KeyIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.sendWelcomeEmail}
                          onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
                          disabled={!!formData.password && formData.password.trim().length > 0}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">
                            Enviar email de bienvenida
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formData.password && formData.password.trim().length > 0 
                              ? "Deshabilitado porque se asignó contraseña" 
                              : "El usuario recibirá un email para establecer su contraseña"
                            }
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Roles y Permisos
                  </Typography>
                </Divider>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!formErrors.roles}>
                  <InputLabel>Roles</InputLabel>
                  <Select
                    multiple
                    value={formData.roles}
                    onChange={(e) => setFormData({ ...formData, roles: e.target.value as string[] })}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            color={getRoleColor(value)}
                            icon={getRoleIcon(value)}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.values(UserRole).map((role) => (
                      <MenuItem key={role} value={role}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getRoleIcon(role)}
                          {role}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.roles && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                      {formErrors.roles}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Usuario Activo"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={submitLoading}
            startIcon={submitLoading ? <CircularProgress size={16} /> : null}
          >
            {editingUser ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;